import React, { useState, useEffect } from 'react';
import {
    Table,
    Box,
    SpaceBetween,
    Header,
    Spinner,
    Alert,
    Badge,
    ExpandableSection,
    Pagination,
    PropertyFilter,
} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import CashPositioningAPIClient from './cashPositioningAPIClient';
import { VarianceOutput, PositioningRegion } from './types';

interface VariancesTableProps {
    region: PositioningRegion;
    date: string;
}

/**
 * Displays variance detection results for a given region and date.
 * Shows deviations between expected and actual account positions.
 */
const VariancesTable: React.FC<VariancesTableProps> = ({ region, date }) => {
    const [variances, setVariances] = useState<VarianceOutput[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedItems, setSelectedItems] = useState<VarianceOutput[]>([]);

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await CashPositioningAPIClient.getVariances(region, date);
                if (!cancelled) {
                    setVariances(response.variances || []);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load variances');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchData();
        return () => { cancelled = true; };
    }, [region, date]);

    const formatAmount = (amount?: number, currency?: string) => {
        if (amount == null) return '—';
        const formatted = Math.abs(amount).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        const prefix = amount < 0 ? '-' : '';
        return `${prefix}${currency || ''} ${formatted}`.trim();
    };

    const getVarianceBadge = (hasVariance?: boolean, variance?: number) => {
        if (!hasVariance) return <Badge color="green">No Variance</Badge>;
        if (variance != null && Math.abs(variance) > 1000000) {
            return <Badge color="red">High Variance</Badge>;
        }
        return <Badge color="blue">Variance</Badge>;
    };

    const columnDefinitions = [
        {
            id: 'accountId',
            header: 'Account ID',
            cell: (item: VarianceOutput) => item.accountId,
            sortingField: 'accountId',
            width: 160,
        },
        {
            id: 'currency',
            header: 'Currency',
            cell: (item: VarianceOutput) => item.currency || '—',
            sortingField: 'currency',
            width: 90,
        },
        {
            id: 'ledgerOpening',
            header: 'Ledger Opening',
            cell: (item: VarianceOutput) => (
                <Box textAlign="right">{formatAmount(item.ledgerOpening, item.currency)}</Box>
            ),
            sortingField: 'ledgerOpening',
            width: 150,
        },
        {
            id: 'ledgerClosing',
            header: 'Ledger Closing',
            cell: (item: VarianceOutput) => (
                <Box textAlign="right">{formatAmount(item.ledgerClosing, item.currency)}</Box>
            ),
            sortingField: 'ledgerClosing',
            width: 150,
        },
        {
            id: 'bankTransactionTotal',
            header: 'Transaction Total',
            cell: (item: VarianceOutput) => (
                <Box textAlign="right">{formatAmount(item.bankTransactionTotal, item.currency)}</Box>
            ),
            sortingField: 'bankTransactionTotal',
            width: 150,
        },
        {
            id: 'expectedClosing',
            header: 'Expected Closing',
            cell: (item: VarianceOutput) => (
                <Box textAlign="right">{formatAmount(item.expectedClosing, item.currency)}</Box>
            ),
            sortingField: 'expectedClosing',
            width: 150,
        },
        {
            id: 'variance',
            header: 'Variance',
            cell: (item: VarianceOutput) => (
                <Box textAlign="right" color={item.hasVariance ? 'text-status-error' : undefined} fontWeight={item.hasVariance ? 'bold' : undefined}>
                    {formatAmount(item.variance, item.currency)}
                </Box>
            ),
            sortingField: 'variance',
            width: 150,
        },
        {
            id: 'hasVariance',
            header: 'Status',
            cell: (item: VarianceOutput) => getVarianceBadge(item.hasVariance, item.variance),
            sortingField: 'hasVariance',
            width: 130,
        },
        {
            id: 'transactionCount',
            header: 'Txn Count',
            cell: (item: VarianceOutput) => item.transactionCount ?? '—',
            sortingField: 'transactionCount',
            width: 100,
        },
        {
            id: 'region',
            header: 'Region',
            cell: (item: VarianceOutput) => item.region || '—',
            sortingField: 'region',
            width: 100,
        },
    ];

    const filteringProperties = [
        { key: 'accountId', propertyLabel: 'Account ID', groupValuesLabel: 'Account values', operators: [':', '='] as const },
        { key: 'currency', propertyLabel: 'Currency', groupValuesLabel: 'Currency values', operators: [':', '='] as const },
        { key: 'hasVariance', propertyLabel: 'Has Variance', groupValuesLabel: 'Variance values', operators: ['='] as const },
        { key: 'region', propertyLabel: 'Region', groupValuesLabel: 'Region values', operators: [':', '='] as const },
    ];

    const { items, collectionProps, propertyFilterProps, paginationProps } = useCollection(
        variances,
        {
            propertyFiltering: { filteringProperties },
            sorting: { defaultState: { sortingColumn: { sortingField: 'variance' }, isDescending: true } },
            pagination: { pageSize: 20 },
            selection: {},
        }
    );

    if (loading) {
        return (
            <Box textAlign="center" padding="l">
                <Spinner size="large" />
                <Box variant="p" margin={{ top: 's' }}>Loading variance data...</Box>
            </Box>
        );
    }

    if (error) {
        return <Alert type="error" header="Error">{error}</Alert>;
    }

    return (
        <SpaceBetween size="m">
            <Table
                {...collectionProps}
                items={items}
                columnDefinitions={columnDefinitions}
                stickyHeader
                resizableColumns
                selectionType="single"
                selectedItems={selectedItems}
                onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
                trackBy="accountId"
                header={
                    <Header
                        counter={`(${variances.length})`}
                        description="Deviations between expected and actual account positions detected during the positioning run"
                    >
                        Variance Detection Results
                    </Header>
                }
                filter={
                    <PropertyFilter
                        {...propertyFilterProps}
                        i18nStrings={{
                            filteringPlaceholder: 'Filter variances...',
                            groupValuesText: 'Values',
                            groupPropertiesText: 'Properties',
                            operatorsText: 'Operators',
                            operationAndText: 'and',
                            operationOrText: 'or',
                            operatorLessText: 'Less than',
                            operatorLessOrEqualText: 'Less than or equal',
                            operatorGreaterText: 'Greater than',
                            operatorGreaterOrEqualText: 'Greater than or equal',
                            operatorContainsText: 'Contains',
                            operatorDoesNotContainText: 'Does not contain',
                            operatorEqualsText: 'Equals',
                            operatorDoesNotEqualText: 'Does not equal',
                            editTokenHeader: 'Edit filter',
                            propertyText: 'Property',
                            operatorText: 'Operator',
                            valueText: 'Value',
                            cancelActionText: 'Cancel',
                            applyActionText: 'Apply',
                            clearFiltersText: 'Clear filters',
                            removeTokenButtonAriaLabel: () => 'Remove token',
                            enteredTextLabel: (text: string) => `Use: "${text}"`,
                        }}
                    />
                }
                pagination={<Pagination {...paginationProps} />}
                empty={
                    <Box textAlign="center" padding="l">
                        <Box variant="p">No variances detected for this region and date.</Box>
                    </Box>
                }
            />

            {/* Transaction detail for selected variance */}
            {selectedItems.length === 1 && selectedItems[0].transactions && selectedItems[0].transactions.length > 0 && (
                <ExpandableSection headerText={`Transactions — ${selectedItems[0].accountId}`} defaultExpanded>
                    <Table
                        items={selectedItems[0].transactions}
                        columnDefinitions={[
                            { id: 'valueDate', header: 'Value Date', cell: (item) => item.valueDate || '—' },
                            { id: 'description', header: 'Description', cell: (item) => item.description || '—' },
                            { id: 'budgetCode', header: 'Budget Code', cell: (item) => item.budgetCode || '—' },
                            { id: 'debit', header: 'Debit', cell: (item) => item.debit != null ? formatAmount(item.debit, selectedItems[0].currency) : '—' },
                            { id: 'credit', header: 'Credit', cell: (item) => item.credit != null ? formatAmount(item.credit, selectedItems[0].currency) : '—' },
                            { id: 'flowAmount', header: 'Net Flow', cell: (item) => formatAmount(item.flowAmount, selectedItems[0].currency) },
                            { id: 'flowStatus', header: 'Status', cell: (item) => item.flowStatus || '—' },
                        ]}
                        variant="embedded"
                    />
                </ExpandableSection>
            )}
        </SpaceBetween>
    );
};

export default VariancesTable;
